/**
 * AiService — routes a request to a hosted provider or the local assistant,
 * enforcing the privacy + connectivity policy from app-core.
 *
 * `AiProvider` is the port a hosted backend implements (OpenRouter, OpenAI…).
 * `LocalAssistantProvider` is a deterministic on-device fallback that always
 * works offline and never sends data anywhere — so the assistant degrades
 * gracefully instead of failing when there is no key or no network.
 */

import {
  type AiCapability,
  type AiContext,
  type AiMessage,
  type AiResult,
  type PrivacyClass,
  redactForAi,
  routeAiRequest,
} from '@life-os/app-core';

export interface AiCompletion {
  readonly text: string;
}

export interface AiProvider {
  readonly name: string;
  complete(req: { capability: AiCapability; messages: readonly AiMessage[] }): Promise<AiCompletion>;
}

/** On-device fallback: rule-based, deterministic, private. Never calls out. */
export class LocalAssistantProvider implements AiProvider {
  readonly name = 'local';
  async complete(req: { capability: AiCapability; messages: readonly AiMessage[] }): Promise<AiCompletion> {
    const lastUser = [...req.messages].reverse().find((m) => m.role === 'user')?.content?.trim() ?? '';
    const lines = lastUser.split(/\n+/).map((l) => l.trim()).filter(Boolean);
    switch (req.capability) {
      case 'summarize':
        return { text: lines.length ? `Key points:\n${lines.slice(0, 5).map((l) => `• ${l}`).join('\n')}` : 'Nothing to summarize yet.' };
      case 'plan':
        return { text: lines.length ? `Suggested order:\n${lines.slice(0, 5).map((l, i) => `${i + 1}. ${l}`).join('\n')}` : 'Add a few items to plan.' };
      case 'tidy':
        return { text: lines.join('\n') };
      case 'chat':
      default:
        return { text: lastUser ? `Noted: ${lastUser}` : 'How can I help?' };
    }
  }
}

export class AiService {
  constructor(
    private readonly local: AiProvider,
    private readonly remote?: AiProvider,
  ) {}

  /** Run a request through the policy. Always returns a result (local fallback). */
  async run(
    capability: AiCapability,
    messages: readonly AiMessage[],
    privacyClass: PrivacyClass,
    ctx: AiContext,
  ): Promise<AiResult> {
    const route = routeAiRequest(privacyClass, ctx);
    if (route.kind === 'remote' && this.remote) {
      const scrubbed = messages.map((m) => ({ ...m, content: redactForAi(m.content, privacyClass) }));
      const completion = await this.remote.complete({ capability, messages: scrubbed });
      return { text: completion.text, usedProvider: 'remote', reason: null };
    }
    const completion = await this.local.complete({ capability, messages });
    const reason = route.kind === 'local' ? route.reason : 'not_configured';
    return { text: completion.text, usedProvider: 'local', reason };
  }
}
